import {
  LOADED,
  CLEAR_DRAWER,
  OPEN_DRAWER,
  SET_DRAWER_INDEX,
  REMOVE_DRAWER_STAGE,
  CLEAR_MODAL,
  OPEN_MODAL,
  OPEN_MODAL_STAGE,
  UPDATE_MODAL,
  SET_MODAL_INDEX,
  REMOVE_MODAL_STAGE,
  GET_BILLING_PLAN_SUCCESS,
  PAGE_ERROR,
} from 'actions';
import { LOCATION_CHANGE } from 'connected-react-router';
import update from 'immutability-helper';
import { DrawerSize } from 'enums';
import { DrawerStage, ModalStage } from 'interfaces';
import createReducer from './createReducer';

const INITIAL_STATE = {
  loaded: false,
  loader: true,
  expired: false,
  pageError: null as string | null,
  drawer: {
    allowExpansion: false,
    size: DrawerSize.WIDE,
    loading: false,
    activeIndex: -1,
    stages: [] as DrawerStage[],
  },
  modal: {
    loading: false,
    activeIndex: -1,
    stages: [] as ModalStage[],
  },
};

type DrawerAction = {
  type: string;
  drawer?: DrawerStage;
};

type IndexAction = {
  type: string;
  index?: number;
};

type ModalAction = {
  type: string;
  modal?: ModalStage;
};

type UpdateModalAction = {
  type: string;
  values?: ModalStage;
  stage?: number;
};

type DataAction = {
  type: string;
  data?: {
    hasExpired: boolean;
  };
};

type ErrorAction = {
  type: string;
  error?: string;
};

type LoaderAction = {
  type: string;
  loader?: boolean;
};

const mainReducer = createReducer(INITIAL_STATE, {
  [LOADED]: (state) => ({ ...state, loaded: true, loader: false }),
  [CLEAR_DRAWER]: (state) => {
    return update(state, {
      drawer: {
        stages: { $set: [] },
        activeIndex: { $set: -1 },
      },
    });
  },
  [OPEN_DRAWER]: (state, action?: DrawerAction) => {
    const drawer = action?.drawer;
    if (!drawer) {
      return state;
    }

    const { ...props } = drawer;
    return update(state, {
      drawer: {
        stages: {
          $push: [{ ...props }],
        },
        activeIndex: { $set: state.drawer.stages.length },
        allowExpansion: { $set: false },
        size: { $set: DrawerSize.WIDE },
      },
    });
  },
  [SET_DRAWER_INDEX]: (state, action?: IndexAction) => {
    const index = action?.index;
    if (index === undefined) {
      return state;
    }
    return update(state, {
      drawer: {
        activeIndex: { $set: index },
      },
    });
  },
  [REMOVE_DRAWER_STAGE]: (state) => {
    return update(state, {
      drawer: {
        stages: { $splice: [[-1, 1]] },
        activeIndex: { $set: state.drawer.activeIndex - 1 },
      },
    });
  },
  [CLEAR_MODAL]: (state) => {
    return update(state, {
      modal: {
        stages: { $set: [] },
        activeIndex: { $set: -1 },
      },
    });
  },
  [LOCATION_CHANGE]: (state) => {
    return update(state, {
      modal: {
        stages: { $set: [] },
        activeIndex: { $set: -1 },
      },
      pageError: { $set: null },
    });
  },
  [OPEN_MODAL]: (state, action?: ModalAction) => {
    const modal = action?.modal;
    if (!modal) {
      return state;
    }
    const { ...props } = modal;
    return update(state, {
      modal: {
        stages: {
          $set: [{ ...props }],
        },
        activeIndex: { $set: 0 },
      },
    });
  },
  [OPEN_MODAL_STAGE]: (state, action?: ModalAction) => {
    const modal = action?.modal;
    if (!modal) {
      return state;
    }
    const { ...props } = modal;
    return update(state, {
      modal: {
        stages: {
          $push: [{ ...props }],
        },
        activeIndex: { $set: state.modal.stages.length },
      },
    });
  },
  [SET_MODAL_INDEX]: (state, action?: IndexAction) => {
    const index = action?.index;
    if (index === undefined) {
      return state;
    }
    return update(state, {
      modal: {
        activeIndex: { $set: index },
      },
    });
  },
  [REMOVE_MODAL_STAGE]: (state) => {
    return update(state, {
      modal: {
        stages: { $splice: [[-1, 1]] },
        activeIndex: { $set: state.modal.activeIndex - 1 },
      },
    });
  },
  [UPDATE_MODAL]: (state, action?: UpdateModalAction) => {
    const values = action?.values;
    const stage = action?.stage;
    if (values === undefined || stage === undefined) {
      return state;
    }
    return update(state, {
      modal: {
        stages: {
          [stage]: { $merge: values },
        },
      },
    });
  },
  [GET_BILLING_PLAN_SUCCESS]: (state, action?: DataAction) => {
    const data = action?.data;
    if (data === undefined) {
      return state;
    }
    return update(state, { expired: { $set: data.hasExpired } });
  },
  [PAGE_ERROR]: (state, action?: ErrorAction) => {
    const error = action?.error;
    if (error === undefined) {
      return state;
    }
    return update(state, { pageError: { $set: error } });
  },
  '*': (state, action?: LoaderAction) => {
    const loader = action?.loader;
    if (!loader || typeof action.loader !== 'boolean') {
      return state;
    }
    return { ...state, loader };
  },
});

export default mainReducer;
